import React from 'react';
import { ApprovalConfiguration } from '../../../../types/workflow';

interface ApprovalConfigProps {
  configuration: ApprovalConfiguration;
  onChange: (config: ApprovalConfiguration) => void;
  isReadOnly: boolean;
}

const ApprovalConfigSimple: React.FC<ApprovalConfigProps> = ({
  configuration,
  onChange,
  isReadOnly,
}) => {
  const updateConfig = (field: keyof ApprovalConfiguration, value: any) => {
    onChange({
      ...configuration,
      [field]: value,
    });
  };

  // Convert comma-separated string to array
  const handleApproversChange = (value: string) => {
    const approvers = value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    updateConfig('approvers', approvers);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Approver Type
        </label>
        <select
          value={configuration.approverType || 'group'}
          onChange={(e) => updateConfig('approverType', e.target.value)}
          disabled={isReadOnly}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        >
          <option value="user">Specific User</option>
          <option value="group">Group</option>
          <option value="role">Role</option>
          <option value="dynamic">Dynamic (from context)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Approvers
        </label>
        <input
          type="text"
          value={configuration.approvers?.join(', ') || ''}
          onChange={(e) => handleApproversChange(e.target.value)}
          disabled={isReadOnly}
          placeholder={
            configuration.approverType === 'group'
              ? "Enter group IDs or names (comma-separated)"
              : configuration.approverType === 'user'
              ? "Enter user emails or IDs (comma-separated)"
              : configuration.approverType === 'role'
              ? "Enter roles: admin, manager, agent, user (comma-separated)"
              // eslint-disable-next-line no-template-curly-in-string
              : "Enter dynamic expression (e.g., ${requester.manager})"
          }
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          {configuration.approverType === 'group' && "Enter group names or IDs, separated by commas"}
          {configuration.approverType === 'user' && "Enter user emails or IDs, separated by commas"}
          {configuration.approverType === 'role' && "Available roles: admin, manager, agent, user"}
          {/* eslint-disable-next-line no-template-curly-in-string */}
          {configuration.approverType === 'dynamic' && "Use workflow variables like ${requester.manager}"}
        </p>
      </div>

      <div>
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={configuration.unanimous || false}
            onChange={(e) => updateConfig('unanimous', e.target.checked)}
            disabled={isReadOnly}
            className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100"
          />
          <span className="ml-2 text-sm text-gray-700">
            Require unanimous approval
          </span>
        </label>
        <p className="mt-1 text-xs text-gray-500 ml-6">
          All approvers must approve for the workflow to continue
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Timeout (hours)
        </label>
        <input
          type="number"
          value={configuration.timeout || ''}
          onChange={(e) => updateConfig('timeout', parseInt(e.target.value) || undefined)}
          disabled={isReadOnly}
          placeholder="Leave empty for no timeout"
          min="1"
          max="720"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Maximum time to wait for approval (1-720 hours)
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Escalation Group
        </label>
        <input
          type="text"
          value={configuration.escalationGroup || ''}
          onChange={(e) => updateConfig('escalationGroup', e.target.value)}
          disabled={isReadOnly}
          placeholder="Enter group ID or name for escalation"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100"
        />
        <p className="mt-1 text-xs text-gray-500">
          Group to escalate to when timeout is reached
        </p>
      </div>

      {/* Display configuration summary */}
      {configuration.approvers && configuration.approvers.length > 0 && (
        <div className="mt-4 p-3 bg-blue-50 rounded-md">
          <h4 className="text-xs font-semibold text-blue-900 mb-1">Configuration Summary:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• Approver Type: <strong>{configuration.approverType}</strong></li>
            <li>• Approvers: <strong>{configuration.approvers.join(', ')}</strong></li>
            {configuration.unanimous && <li>• Requires unanimous approval</li>}
            {configuration.timeout && <li>• Timeout: <strong>{configuration.timeout} hours</strong></li>}
            {configuration.escalationGroup && <li>• Escalates to: <strong>{configuration.escalationGroup}</strong></li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ApprovalConfigSimple;